import { css } from '@emotion/react';
import { Collapse, Icon, Select } from 'antd';
import Router from 'next/router';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import {
  getFieldOptions,
  loadResults,
  resetResults,
  resetSearchCriteria,
  setCurrentPage,
  setSortField,
  setSortOrder,
  updateSearchCriteria,
} from '@/src/namedParty/redux/actions';
import Button from '@/src/shared/components/Button';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import { form as formStyle, space } from '@/styles';
import formatURL from '@/utils/formatURL';
import { escapeGTMParam, trackGTMEvent } from '@/utils/utils';

import style from './style';

const filterColorOverride = css`
  background-color: #fff
`

const { m } = space;

const { Option } = Select;

const FIELD_NAMES = {
  CASE_NAME: 'CaseNames',
  STATE: 'States',
  ISSUE: 'Issues',
  STATUS: 'Statuses',
};
const filterCollapseProps = {
  bordered: false,
  expandIconPosition: 'right',
  expandIcon: ({ isActive }) => <Icon type={isActive ? 'minus' : 'plus'} />,
};

const SearchFilters = props => {
  const {
    handleSearchFiltersCollapse,
    fieldOptionsData: {
      options: { statuses, states, caseNames, issues },
      isFetchingOptions,
    },
    form,
    sortField,
    sortOrder,
  } = props;
  const { searchCriteria } = props;

  const handleSearch = async e => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    await form.validateFieldsAndScroll(async (err, values) => {
      await props.setCurrentPage(1);
      const { States, Issues, Statuses, CaseNames } = values;

      if (err) return;
      const payload = {
        searchOptions: {
          AmicusFieldOptions: {
            CaseTypes: [
              {
                CaseTypeCode: '2',
              },
            ],
            Statuses: Statuses
              ? Statuses.map(s => {
                return { StatusCode: s };
              })
              : [],
            Issues: Issues
              ? Issues.map(i => {
                return { IssueId: i };
              })
              : [],
            States: States
              ? States.map(s => {
                return { StateCode: s };
              })
              : [],
            CaseNames: CaseNames
              ? CaseNames.map(c => {
                return { Value: c };
              })
              : [],
            SortOrder: sortOrder,
            SortField: sortField,
            PageSize: 10,
            Page: 1,
          },
        },
      };

      trackGTMEvent({
        event: 'NamedPartySearch',
        caseName:
          CaseNames?.length > 0
            ? caseNames
              .filter(c => CaseNames.includes(c.Value))
              .map(c => escapeGTMParam(c.Text))
              .join(', ')
            : 'All',
        state: States?.length > 0 ? States.join(', ') : 'All',
        issue:
          Issues?.length > 0
            ? issues
              .filter(i => Issues.includes(i.IssueId))
              .map(i => escapeGTMParam(i.IssueName))
              .join(', ')
            : 'All',
        status:
          Statuses?.length > 0
            ? statuses
              .filter(s => Statuses.includes(s.StatusCode))
              .map(s => escapeGTMParam(s.StatusDescription))
              .join(', ')
            : 'All',
      });

      const queryObj = {
        statuses: Statuses.join(','),
        issues: Issues.join(', '),
        states: States.join(','),
        cases: CaseNames.join(','),
      };
      const formattedURL = formatURL('/advocacy/legal-judicial-advocacy/named-party', queryObj);
      Router.push(formattedURL, formattedURL, { shallow: true });
      props.loadResults(payload);
    });
  };

  const handleResetAll = () => {
    const formattedURL = '/advocacy/legal-judicial-advocacy/named-party';
    props.resetResults();
    Router.push(formattedURL, formattedURL, { shallow: true });
    form.setFieldsValue({
      CaseNames: [],
      States: [],
      Issues: [],
      Statuses: [],
    });
    props.resetSearchCriteria();
    handleSearch(null);
  };

  useEffect(() => {
    const fetchOptions = async () => {
      await props.getFieldOptions();
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    handleSearch(null);
  }, []);

  const renderFilterSection = opts => {
    const { label, name, options } = opts;
    return (
      <Collapse {...filterCollapseProps} css={filterColorOverride}>
        <Collapse.Panel
          header={
            <Heading type={4} weight="bolder" theme="primaryGray">
              {label}
            </Heading>
          }
        >
          <Form.Item>
            {form.getFieldDecorator(name, {
              initialValue: searchCriteria[name],
            })(
              <Select
                css={formStyle.inputGray}
                loading={isFetchingOptions}
                mode="multiple"
                size="large"
                allowClear
                optionFilterProp="children"
                placeholder="All"
                onChange={(v, s) => {
                  props.updateSearchCriteria(name, v);
                }}
              // {...multiSelectProps(isFetchingOptions)}
              >
                {options}
              </Select>
            )}
          </Form.Item>
        </Collapse.Panel>
      </Collapse>
    );
  };

  return (
    <div css={style.filters}>
      <Collapse
        defaultActiveKey={['1']}
        {...filterCollapseProps}
        onChange={handleSearchFiltersCollapse}
        css={filterColorOverride}
      >
        <Collapse.Panel
          isActive
          key="1"
          header={
            <Heading type={3} weight="bolder" theme="primaryGray">
              Search Filters
            </Heading>
          }
        >
          <Form onSubmit={handleSearch}>
            {renderFilterSection({
              label: 'Case Name',
              name: FIELD_NAMES.CASE_NAME,
              options: caseNames.map(c => (
                <Option key={`${c.Value}-case`} value={c.Value}>
                  {c.Text}
                </Option>
              )),
            })}
            {renderFilterSection({
              label: 'State',
              name: FIELD_NAMES.STATE,
              options: states.map(s => (
                <Option key={`${s.StateCode}-state`} value={s.StateCode}>
                  {s.StateDescription}
                </Option>
              )),
            })}
            {renderFilterSection({
              label: 'Issue',
              name: FIELD_NAMES.ISSUE,
              options: issues.map(i => (
                <Option key={`${i.IssueId}-issue`} value={i.IssueId}>
                  {i.IssueName}
                </Option>
              )),
            })}
            {renderFilterSection({
              label: 'Status',
              name: FIELD_NAMES.STATUS,
              options: statuses.map(s => (
                <Option key={`${s.StatusCode}-status`} value={s.StatusCode}>
                  {s.StatusDescription}
                </Option>
              )),
            })}
          </Form>
        </Collapse.Panel>
      </Collapse>
      <div css={[m.t16]}>
        <Button
          type="button"
          size="small"
          css={[m.r16, m.b16]}
          htmlType="submit"
          onClick={handleSearch}
        >
          Search
        </Button>
        <Button size="small" theme="gray" onClick={handleResetAll}>
          <Icon type="sync" /> Reset All
        </Button>
      </div>
    </div>
  );
};

SearchFilters.propTypes = {};

function mapStateToProps(state) {
  const {
    namedParty: { fieldOptionsData, searchCriteria, sortField, sortOrder },
  } = state;
  return {
    fieldOptionsData,
    searchCriteria,
    sortField,
    sortOrder,
  };
}

export default connect(
  mapStateToProps,
  {
    getFieldOptions,
    loadResults,
    resetResults,
    updateSearchCriteria,
    setCurrentPage,
    resetSearchCriteria,
    setSortField,
    setSortOrder,
  }
)(Form.create({ name: 'NamedPartySearchForm' })(SearchFilters));
